import React, { useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { RoutePaths } from "../../../../enumStrings";
import { useCompleteStripeConnectOAuthMutation } from "../../../../graphql/types";
import Loader from "../../../common/Loader";

const FullPagePaymentsLoader = () => (
  <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-white px-6">
    <div className="flex max-w-sm flex-col items-center text-center">
      <Loader />
      <h1 className="mt-6 text-2xl font-semibold text-gray-900">
        Setting up payments
      </h1>
      <p className="mt-2 text-sm leading-6 text-gray-600">
        Please keep this page open. We will take you back to Chamber Set Up as
        soon as credit card payments are ready.
      </p>
    </div>
  </div>
);

const StripeConnectCallback = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const hasStartedCompletionRef = useRef(false);
  const chamberSettingsPath = RoutePaths.chamberAdmin_myChamber_chamberSettings;
  const [completeStripeConnectOAuth, { loading, error, called }] =
    useCompleteStripeConnectOAuthMutation();

  const code = searchParams.get("code");
  const state = searchParams.get("state");
  const stripeError = searchParams.get("error");
  const stripeErrorDescription = searchParams.get("error_description");
  const missingCallbackParams = !stripeError && (!code || !state);
  const callbackError =
    stripeErrorDescription ||
    stripeError ||
    error?.message ||
    (missingCallbackParams
      ? "Stripe did not return the information needed to complete setup."
      : null);

  useEffect(() => {
    if (
      stripeError ||
      missingCallbackParams ||
      !code ||
      !state ||
      called ||
      hasStartedCompletionRef.current
    ) {
      return;
    }

    hasStartedCompletionRef.current = true;

    completeStripeConnectOAuth({
      variables: {
        code,
        state,
      },
    })
      .then(() => {
        navigate(`${chamberSettingsPath}?stripeConnect=success`, {
          replace: true,
        });
      })
      .catch(() => undefined);
  }, [
    called,
    chamberSettingsPath,
    code,
    completeStripeConnectOAuth,
    missingCallbackParams,
    navigate,
    state,
    stripeError,
  ]);

  if (loading || (!callbackError && !called)) {
    return <FullPagePaymentsLoader />;
  }

  if (callbackError) {
    return (
      <div className="mx-auto max-w-3xl rounded-lg border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700">
        <p className="font-semibold">Unable to complete Stripe setup.</p>
        <p className="mt-1">{callbackError}</p>
        <button
          type="button"
          onClick={() => navigate(chamberSettingsPath)}
          className="mt-4 rounded-lg border border-red-200 bg-white px-4 py-2 text-sm font-semibold text-red-700 transition hover:bg-red-100"
        >
          Back to Chamber Set Up
        </button>
      </div>
    );
  }

  return <FullPagePaymentsLoader />;
};

export default StripeConnectCallback;
